/**
 * DELIVERY PANEL — what one customer PAID and what they actually RECEIVED.
 *
 * Replaces opening mongosh against the production container to answer "was
 * this customer given the paid report or the free one?". Three collections,
 * two payment gateways, and a join that is not where anyone looks for it.
 *
 * ── IT DECIDES NOTHING ──────────────────────────────────────────────────────
 *
 * Every verdict on this panel — paid, the entitlement at delivery, each
 * mismatch and its wording — comes finished from
 * GET /api/admin/audits/:id/delivery, which computes it in backend/delivery.py
 * where it is tested. Nothing here re-derives "paid" from an amount or infers a
 * mismatch from two fields, because a second opinion rendered in JSX is a
 * second paywall with a second set of bugs — the shape this codebase has
 * already paid for twice (server trimming on one predicate while the client
 * paywalled on another, 2026-09-03 and 2026-09-05).
 *
 * The one thing this file owns is which of those facts is red.
 *
 * ── HOOKS ───────────────────────────────────────────────────────────────────
 *
 * Both hooks are at the top and there are no early returns: the loading, error
 * and loaded states are branches inside one JSX tree. Per CLAUDE.md — a
 * `useMemo` placed next to the markup that reads it is what unmounted every
 * audit report on production for several hours (React #310).
 *
 * ── A SEPARATE FILE ─────────────────────────────────────────────────────────
 *
 * AdminDashboard.js is already 1500 lines against a 500-line guideline, and
 * this panel is self-contained. It takes an audit id and renders; it shares no
 * state with the table it opens from.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { X as XIcon, Eye, AlertTriangle, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** A label/value row. `mono` for ids, which are read character by character. */
const Field = ({ label, value, mono = false, muted = false }) => (
  <div className="flex items-baseline gap-3 py-1.5">
    <span className="text-sm text-slate-400 w-40 flex-shrink-0">{label}</span>
    <span
      className={`text-sm break-all ${mono ? 'font-mono text-[12px]' : ''} ${
        muted ? 'text-slate-300' : 'text-[var(--ink-1)]'
      }`}
    >
      {value}
    </span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="py-4 border-t border-slate-100 first:border-t-0 first:pt-0">
    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-1">{title}</h3>
    {children}
  </div>
);

/** ISO string to something readable, or the honest absence of one. */
const when = (iso) => (iso ? new Date(iso).toLocaleString() : null);

const money = (p) => {
  if (p.amount === null || p.amount === undefined) return '—';
  const amount = `${p.currency || 'USD'} ${Number(p.amount).toFixed(2)}`;
  return p.moved_money ? amount : `${amount} (no money moved)`;
};

const DeliveryPanel = ({ auditId, appName, onClose }) => {
  const [record, setRecord] = useState(null);
  const [state, setState] = useState('loading'); // loading | error | ready
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const resp = await axios.get(`${API}/admin/audits/${auditId}/delivery`, {
          withCredentials: true,
        });
        if (!live) return;
        setRecord(resp.data);
        setState('ready');
      } catch (e) {
        if (!live) return;
        // The 503 the endpoint raises when a payment collection cannot be read
        // carries its own sentence, and it matters: an unreadable collection is
        // NOT an absent payment, and showing "no payment record" here would
        // manufacture the exact mismatch this panel exists to find.
        setError(e?.response?.data?.detail || e?.message || 'Could not load delivery record');
        setState('error');
      }
    })();
    return () => {
      live = false;
    };
  }, [auditId]);

  const paid = record?.is_paid;
  const mismatches = record?.mismatches || [];
  const times = record?.delivered_at || {};
  const payments = record?.payments || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="delivery-panel"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-bold text-[var(--ink-1)] text-base">
              Delivery — {appName || 'Audit'}
            </h2>
            <p className="text-sm text-slate-400 mt-0.5 font-mono">{auditId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <XIcon className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {state === 'loading' && (
            <div className="py-12 text-center text-slate-400 text-sm">Loading delivery record…</div>
          )}

          {state === 'error' && (
            <div className="py-12 text-center text-red-600 text-sm" data-testid="delivery-error">
              {error}
            </div>
          )}

          {state === 'ready' && record && (
            <>
              {/* MISMATCHES FIRST, IN RED. They are the reason to open this
                  panel, and a reader who has to scroll to find out that a
                  customer paid for a failed report will not scroll. */}
              {mismatches.length > 0 && (
                <div
                  className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4"
                  data-testid="delivery-mismatches"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-red-600">
                      {mismatches.length === 1
                        ? 'Payment and delivery disagree'
                        : `${mismatches.length} ways payment and delivery disagree`}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {mismatches.map((m) => (
                      <li key={m.code} data-testid={`mismatch-${m.code}`}>
                        <p className="text-sm font-medium text-red-600">{m.label}</p>
                        <p className="text-sm text-red-600/80 mt-0.5">{m.detail}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Section title="Entitlement">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`px-2.5 py-1 text-sm font-semibold rounded-full ${
                      paid
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                    data-testid="delivery-paid-badge"
                  >
                    {paid ? 'Paid' : 'Free'}
                  </span>
                  <span className="text-sm text-slate-400">
                    is_paid: {String(paid)}
                  </span>
                </div>
                <Field label="Served as" value={`${record.entitlement_at_delivery} report`} />
                <Field
                  label="payment_session_id"
                  value={record.payment_session_id || 'none'}
                  mono={!!record.payment_session_id}
                  muted={!record.payment_session_id}
                />
                {/* `is_paid` is written once at creation and never updated, so
                    this IS the entitlement the report was delivered under, not
                    a recomputation from today's state. Said on the panel
                    because a reader has no other way to know that. */}
                <p className="text-sm text-slate-400 mt-2">
                  is_paid is set once, when the audit is created, and no code path updates it.
                  This is the entitlement the report was delivered under.
                </p>
              </Section>

              <Section title="Customer">
                <Field label="Email" value={record.customer?.email || 'unknown'} muted={!record.customer?.email} />
                <Field label="Name" value={record.customer?.name || '—'} muted={!record.customer?.name} />
                <Field label="user_id" value={record.customer?.user_id || '—'} mono />
              </Section>

              <Section title="Payment">
                {payments.length === 0 ? (
                  <p className="text-sm text-slate-400">
                    No row in paypal_payments or razorpay_orders points at this audit, by
                    used_for_audit or by payment_session_id.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {payments.map((p, i) => (
                      <div
                        key={p.payment_id || p.order_id || i}
                        className="rounded-xl border border-slate-200 p-3"
                        data-testid={`delivery-payment-${i}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-[var(--ink-1)]">{p.provider}</span>
                          <span
                            className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                              p.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {p.status || 'unknown'}
                          </span>
                          {p.gateway === 'promo' && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                              promo
                            </span>
                          )}
                        </div>
                        <Field label="Amount" value={money(p)} />
                        <Field label="Plan" value={p.plan || '—'} />
                        <Field label="payment_id" value={p.payment_id || '—'} mono />
                        {p.order_id && <Field label="order_id" value={p.order_id} mono />}
                        <Field label="used_for_audit" value={p.used_for_audit || 'not stamped'} mono={!!p.used_for_audit} muted={!p.used_for_audit} />
                        <Field label="Created" value={when(p.created_at) || '—'} />
                        <Field label="Completed" value={when(p.completed_at) || 'not completed'} muted={!p.completed_at} />
                      </div>
                    ))}
                    {payments.length > 1 && (
                      <p className="text-sm text-red-600">
                        {payments.length} payment rows match this audit. More than one is
                        worth reading as a possible duplicate charge.
                      </p>
                    )}
                  </div>
                )}
              </Section>

              <Section title="Delivered">
                <Field label="Report finished" value={when(times.report_completed) || 'never'} muted={!times.report_completed} />
                {times.retry_completed && (
                  <Field label="Re-run finished" value={when(times.retry_completed)} />
                )}
                <Field
                  label="Email sent"
                  value={
                    when(times.email_sent) ||
                    'not recorded (email_sent_at was added 2026-10-02; audits delivered before then have only a status)'
                  }
                  muted={!times.email_sent}
                />
                <Field
                  label="Email status"
                  value={record.email?.retry_status || record.email?.status || 'not sent'}
                  muted={!record.email?.status && !record.email?.retry_status}
                />
                {record.email?.error && (
                  <p className="text-sm text-red-600 mt-1">{record.email.error}</p>
                )}
              </Section>

              <Section title="Report">
                <Field label="Status" value={record.status || '—'} />
                <Field label="Overall score" value={record.report?.overall_score ?? '—'} />
                <Field label="Findings" value={record.report?.is_empty ? 'none' : 'present'} />
                <Field label="Pipeline stage" value={record.report?.pipeline_stage || '—'} />
                <Field label="Screenshots" value={record.report?.has_screenshots ? 'stored' : 'none'} />
              </Section>

              <div className="pt-4 border-t border-slate-100">
                {/* The link the panel is FOR. The server decides what
                    `as=customer` means; this only asks for it. It renders with
                    the audit owner's entitlement, which is `is_paid` and
                    therefore what was delivered — see
                    server.preview_viewer_id. */}
                <a
                  href={record.customer_view_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--ink-1)] text-white text-sm font-medium rounded-lg hover:bg-[#2a2a4e] transition-colors"
                  data-testid="view-what-the-customer-received"
                >
                  <Eye className="w-3.5 h-3.5" />
                  View exactly what the customer received
                </a>
                <p className="text-sm text-slate-400 mt-2">
                  Opens the report with this audit owner&rsquo;s entitlement, not yours.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryPanel;
